import { Injectable } from '@angular/core';

import { DocumentItem } from '../interfaces/document-item.interface';
import {
  DeliveryRouteCandidate,
  DeliveryRouteResolution,
  MaterialDeliveryWindow,
} from '../interfaces/delivery-route.interface';

// índice de Date (0 = domingo) a la clave de traducción del día
export const WEEKDAY_KEYS = [
  'DOCUMENT_GENERATE.WEEKDAYS.SUNDAY',
  'DOCUMENT_GENERATE.WEEKDAYS.MONDAY',
  'DOCUMENT_GENERATE.WEEKDAYS.TUESDAY',
  'DOCUMENT_GENERATE.WEEKDAYS.WEDNESDAY',
  'DOCUMENT_GENERATE.WEEKDAYS.THURSDAY',
  'DOCUMENT_GENERATE.WEEKDAYS.FRIDAY',
  'DOCUMENT_GENERATE.WEEKDAYS.SATURDAY',
] as const;

const NO_WINDOW = 'SIN_VENTANA';

export interface WindowGroup {
  lines: DocumentItem[];
  days: number[];
  signature: string;
}

@Injectable()
export class DeliveryRouteSplitService {
  // ruta de entrega vigente de cada documento, por índice; sin entrada el documento va sin ruta
  private resolvedRoutes = new Map<number, DeliveryRouteCandidate | null>();
  private resolution: DeliveryRouteResolution | null = null;
  private resolutionSignature = '';
  // la resolución vigente se pidió con el mismo juego de documentos que hay ahora:
  // sus índices son directamente los de los documentos finales
  private resolutionMatchesDocuments = true;
  // ventanas que devolvió la última resolución; con ellas se parte por ventana horaria
  private windows: MaterialDeliveryWindow[] = [];
  // días admitidos de cada documento partido por ventana, por índice
  private windowDays = new Map<number, number[]>();
  // de qué documento salió cada uno tras partir, y si es el que conserva la ruta
  private splitOrigin = new Map<number, { originIndex: number; primary: boolean }>();
  // ruta que el usuario dejó elegida en el modal, por índice de documento ya repartido
  private chosen = new Map<number, DeliveryRouteCandidate | null>();
  // la partición por ventana solo se aplica cuando la entrega quedó bloqueada por horario
  private splitEnabled = false;

  get currentResolution(): DeliveryRouteResolution | null {
    return this.resolution;
  }

  get materialWindows(): MaterialDeliveryWindow[] {
    return this.windows;
  }

  get windowSplitEnabled(): boolean {
    return this.splitEnabled;
  }

  set windowSplitEnabled(value: boolean) {
    this.splitEnabled = value;
  }

  get chosenRoutes(): Map<number, DeliveryRouteCandidate | null> {
    return this.chosen;
  }

  reset(): void {
    this.resolvedRoutes.clear();
    this.resolution = null;
    this.resolutionSignature = '';
    this.resolutionMatchesDocuments = true;
    this.windows = [];
    this.windowDays.clear();
    this.splitOrigin.clear();
    this.chosen.clear();
    this.splitEnabled = false;
  }

  // la resolución guardada sirve mientras la firma del estado que la condiciona no cambie
  cachedResolutionFor(signature: string): DeliveryRouteResolution | null {
    return this.resolution && this.resolutionSignature === signature ? this.resolution : null;
  }

  // deja en el mapa la candidata que eligió el endpoint para cada documento
  storeResolution(resolution: DeliveryRouteResolution, signature: string): void {
    this.resolution = resolution;
    this.resolutionSignature = signature;
    this.resolutionMatchesDocuments = true;
    this.resolvedRoutes.clear();
    for (const document of resolution.documents ?? []) {
      const selected = document.routes.find(
        (route) => route.eligible && route._id === document.selectedRouteId
      );
      this.resolvedRoutes.set(document.index, selected ?? null);
    }
    this.collectMaterialWindows(resolution);
  }

  // el pedido se repartió pero el backend no llegó a resolver los documentos nuevos:
  // la resolución vigente sigue siendo la del reparto anterior
  markResolutionStale(): void {
    this.resolutionMatchesDocuments = false;
  }

  // ningún documento consiguió ruta y la causa está en la ventana de entrega:
  // los viajes existen pero no caen en el horario que imponen los artículos
  blockedByDeliveryWindow(resolution: DeliveryRouteResolution): boolean {
    const documents: { routes?: DeliveryRouteCandidate[]; windowConflict?: boolean }[] = resolution
      .documents?.length
      ? resolution.documents
      : [{ routes: resolution.routes, windowConflict: resolution.windowConflict }];

    return documents.some((document) => {
      const routes = document.routes ?? [];
      if (routes.some((route) => route.eligible)) return false;
      if (document.windowConflict) return true;
      return routes.some((route) => route.rejectedReason === 'NO_TRIP_IN_WINDOW');
    });
  }

  // segunda pasada: la ventana no es un valor discreto como el almacén, se estrecha
  // al agrupar, así que subdivide lo que ya repartieron almacén y existencia
  applyWindowSplit(documents: DocumentItem[][], routeDays?: number[]): DocumentItem[][] {
    this.windowDays.clear();
    this.splitOrigin.clear();
    // sin bloqueo por horario el pedido no se parte: un documento con ventanas
    // distintas pero un viaje que las cumple todas viaja completo
    if (!this.splitEnabled || !this.windows.length) return documents;

    const result: DocumentItem[][] = [];

    documents.forEach((lines, originIndex) => {
      // el bono viaja en el mismo documento que su línea de origen
      const purchased = lines.filter((line) => !line.isBonus);
      const bonusesOf = (line: DocumentItem) =>
        lines.filter((it) => it.isBonus && it.bonusParentId === line.lineId);

      const groups = DeliveryRouteSplitService.groupLinesByDeliveryWindow(
        purchased,
        this.windows,
        routeDays
      );
      if (groups.length <= 1) {
        this.splitOrigin.set(result.length, { originIndex, primary: true });
        result.push(lines);
        return;
      }
      groups.forEach((group, groupIndex) => {
        const withBonuses: DocumentItem[] = [];
        for (const line of group.lines) withBonuses.push(line, ...bonusesOf(line));
        this.windowDays.set(result.length, group.days);
        // el primer grupo es el sembrado con la ruta resuelta: solo ese la conserva
        this.splitOrigin.set(result.length, { originIndex, primary: groupIndex === 0 });
        result.push(withBonuses);
      });
    });
    return result;
  }

  // el documento de la resolución que le toca al documento repartido. Mientras la
  // resolución corresponda al reparto vigente es él mismo; solo cuando quedó obsoleta
  // hay que retroceder al documento del que salió
  private resolutionIndexOf(index: number): number {
    if (this.resolutionMatchesDocuments) return index;
    return this.splitOrigin.get(index)?.originIndex ?? index;
  }

  // con la resolución obsoleta, el documento que se desprendió por ventana no puede
  // heredar las candidatas del origen: se resolvieron para otros artículos
  private inheritsNothing(index: number): boolean {
    return !this.resolutionMatchesDocuments && this.splitOrigin.get(index)?.primary === false;
  }

  // ruta del documento ya partido
  routeFor(index: number): DeliveryRouteCandidate | null {
    if (this.chosen.has(index)) return this.chosen.get(index) ?? null;
    if (this.inheritsNothing(index)) return null;
    return this.resolvedRoutes.get(this.resolutionIndexOf(index)) ?? null;
  }

  candidatesFor(index: number): DeliveryRouteCandidate[] {
    if (this.inheritsNothing(index)) return [];
    const document = this.resolution?.documents?.find(
      (doc) => doc.index === this.resolutionIndexOf(index)
    );
    return (document?.routes ?? []).filter((route) => route.eligible);
  }

  // fecha estimada del documento cuando no hay viaje que la respalde
  estimatedDeliveryDate(index: number): Date | null {
    const document = this.resolution?.documents?.find(
      (doc) => doc.index === this.resolutionIndexOf(index)
    );
    const estimated = document?.estimatedDeliveryDate ?? this.resolution?.estimatedDeliveryDate;
    return estimated ? new Date(estimated) : null;
  }

  windowDaysOf(index: number): number[] | undefined {
    return this.windowDays.get(index);
  }

  splitByWindow(index: number): boolean {
    return this.windowDays.has(index);
  }

  resolvedRouteAt(index: number): DeliveryRouteCandidate | null {
    return this.resolvedRoutes.get(index) ?? null;
  }

  setResolvedRoute(index: number, route: DeliveryRouteCandidate | null): void {
    this.resolvedRoutes.set(index, route);
  }

  // aplica la ruta elegida en cada documento ya repartido
  applyChoices(choices: Record<number, string | null>, documentCount: number): void {
    this.chosen.clear();
    for (let index = 0; index < documentCount; index++) {
      const routeId = choices[index] ?? null;
      if (!routeId) {
        this.chosen.set(index, null);
        continue;
      }
      const chosen = this.candidatesFor(index).find((route) => route._id === routeId);
      this.chosen.set(index, chosen ?? null);
    }
  }

  // las ventanas de todos los documentos, sin repetir artículo: alimentan la partición
  private collectMaterialWindows(resolution: DeliveryRouteResolution): void {
    const byMaterial = new Map<string, MaterialDeliveryWindow>();
    const sources = resolution.documents?.length
      ? resolution.documents.map((document) => document.materialWindows)
      : [resolution.materialWindows];

    for (const windows of sources) {
      for (const window of windows ?? []) {
        if (window?._id && !byMaterial.has(String(window._id))) {
          byMaterial.set(String(window._id), window);
        }
      }
    }
    this.windows = [...byMaterial.values()];
  }

  // la data maestra usa 1..7 con domingo en 7; Date usa 0..6 con domingo en 0
  static normalizeDays(days: number[] | undefined): number[] {
    const normalized = (days ?? [])
      .map((day) => {
        const value = Number(day);
        if (!Number.isFinite(value)) return -1;
        const wrapped = value % 7;
        return wrapped < 0 ? wrapped + 7 : wrapped;
      })
      .filter((day) => day >= 0);
    return [...new Set(normalized)].sort((a, b) => a - b);
  }

  static indexWindows(
    windows: MaterialDeliveryWindow[] | undefined
  ): Map<string, MaterialDeliveryWindow> {
    const byMaterial = new Map<string, MaterialDeliveryWindow>();
    for (const window of windows ?? []) {
      if (window?._id) byMaterial.set(String(window._id), window);
    }
    return byMaterial;
  }

  // dos artículos van juntos si su ventana es la misma: mismos días y mismo horario.
  // Agrupar por intersección no vacía no sirve — un artículo restringido a domingo
  // 11:14-16:00 "intersecta" con otro de domingo a jueves, y el documento entero
  // queda atado a esa franja aunque el viaje disponible no la cumpla
  private static windowSignature(window: MaterialDeliveryWindow | undefined): string {
    const days = DeliveryRouteSplitService.normalizeDays(window?.days);
    if (!days.length) return NO_WINDOW;
    return `${days.join(',')}|${window?.hourStart ?? ''}-${window?.hourEnd ?? ''}`;
  }

  static groupLinesByDeliveryWindow(
    lines: DocumentItem[],
    windows: MaterialDeliveryWindow[] | undefined,
    routeDays?: number[]
  ): WindowGroup[] {
    const byMaterial = DeliveryRouteSplitService.indexWindows(windows);
    if (!lines.length) return [];

    const groups = new Map<string, WindowGroup>();
    for (const line of lines) {
      const window = byMaterial.get(String(line._id));
      const signature = DeliveryRouteSplitService.windowSignature(window);
      const existing = groups.get(signature);
      if (existing) {
        existing.lines.push(line);
        continue;
      }
      groups.set(signature, {
        lines: [line],
        days: DeliveryRouteSplitService.normalizeDays(window?.days),
        signature,
      });
    }

    const ordered = [...groups.values()];
    if (ordered.length <= 1) return ordered;

    // el grupo compatible con la ruta ya resuelta va primero: es el que la conserva
    const seedDays = DeliveryRouteSplitService.normalizeDays(routeDays);
    const fitsRoute = (group: WindowGroup) =>
      !group.days.length || !seedDays.length || group.days.some((day) => seedDays.includes(day));

    // los artículos sin ventana propia acompañan al grupo principal en lugar de
    // abrir un documento suyo: no imponen restricción alguna
    const free = ordered.filter((group) => group.signature === NO_WINDOW);
    const restricted = ordered.filter((group) => group.signature !== NO_WINDOW);
    if (!restricted.length) return free;

    restricted.sort((a, b) => {
      const byFit = Number(fitsRoute(b)) - Number(fitsRoute(a));
      if (byFit !== 0) return byFit;
      // a igualdad, el grupo con más artículos manda: el documento principal es el mayor
      return b.lines.length - a.lines.length;
    });

    for (const group of free) restricted[0].lines.push(...group.lines);
    return restricted;
  }
}
